import { useTranslation } from 'react-i18next';
import { Flex, Icon, Spinner, Text } from '@kognitos/lattice';
import type { FileDropzoneProps } from '../../types';

/**
 * Large drag-and-drop upload area component
 *
 * Displays a prominent upload zone with visual feedback for drag-and-drop.
 * Shows upload progress when files are being uploaded.
 *
 * @param props - Component props
 * @returns JSX element containing the dropzone
 */
export function FileDropzone({
  isUploading,
  compact = false,
  uploadingCount,
  isDragging,
  onClick,
  onDrop,
  onDragOver,
  onDragLeave,
  error,
}: FileDropzoneProps) {
  const { t } = useTranslation('automation-inputs');

  const getBorderClass = () => {
    if (error) return 'border-destructive';
    if (isDragging) return 'border-primary';
    return 'border-border';
  };

  return (
    <div
      onClick={onClick}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={`
        flex flex-col items-center justify-center
        border-dashed cursor-pointer transition-colors
        ${
          compact
            ? 'w-fit h-8 gap-2 rounded-md border px-3'
            : 'w-full h-[128px] p-8 rounded-lg border-2'
        }
        ${getBorderClass()}
        ${isDragging ? 'bg-primary/10' : 'bg-muted/50'}
        ${isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50 hover:bg-muted'}
      `}
      role="button"
      tabIndex={0}
      aria-label={t('actions.uploadFiles')}
    >
      {isUploading ? (
        // Compact is a 32px-tall button, so the spinner sits beside the label
        // rather than above it — stacked, the pair overflows the box.
        <Flex
          direction={compact ? 'row' : 'column'}
          align="center"
          gap={2}
          className={compact ? 'min-w-0' : undefined}
        >
          <Spinner size={compact ? 'sm' : undefined} />
          <Text
            data-testid="uploading-file-message"
            level="small"
            color="muted"
            className={compact ? 'truncate' : undefined}
          >
            {t('messages.uploading', { count: uploadingCount })}
          </Text>
        </Flex>
      ) : (
        <Flex direction="column" align="center" gap={2}>
          <Flex align="center" gap={2}>
            <Icon type="Upload" size={compact ? 'sm' : undefined} />
            <Text
              level={compact ? 'small' : 'base'}
              weight={compact ? 'medium' : 'semibold'}
            >
              {t('actions.upload')}
            </Text>
          </Flex>
          {!compact && (
            <Text level="base" color="muted">
              {t('messages.dragDrop')}
            </Text>
          )}
        </Flex>
      )}
    </div>
  );
}
